import React, { useState, useEffect } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { useAuth } from '../../contexts/AuthContext';
import { useTranslation } from 'react-i18next';
import {
  Users, UserPlus, Mail, Copy, Check, Trash2,
  Clock, AlertCircle, Loader2, Link as LinkIcon, X
} from 'lucide-react';
import axios from 'axios';
import { toast } from 'sonner';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const APP_URL = process.env.REACT_APP_BACKEND_URL?.replace('/api', '') || window.location.origin;

const ROLE_LABELS = { CONTRACTOR_ADMIN: 'Admin', CONTRACTOR_MEMBER: 'Üye' };
const ROLE_COLORS = { CONTRACTOR_ADMIN: 'bg-green-100 text-green-700', CONTRACTOR_MEMBER: 'bg-blue-100 text-blue-700' };

const RemoveConfirmModal = ({ member, onConfirm, onClose }) => (
  <div className="fixed inset-0 bg-black/40 z-50 flex items-center justify-center p-4" onClick={onClose}>
    <div className="bg-white rounded-2xl shadow-2xl w-full max-w-sm p-6" onClick={e => e.stopPropagation()}>
      <div className="flex items-center justify-between mb-4">
        <h3 className="text-base font-bold text-gray-900">Üyeyi Çıkar</h3>
        <button onClick={onClose} className="text-gray-400 hover:text-gray-600"><X className="h-4 w-4" /></button>
      </div>
      <p className="text-sm text-gray-500 mb-5">
        <span className="font-semibold text-gray-800">{member?.name}</span> isimli üyeyi ekibinizden çıkarmak istediğinizden emin misiniz?
      </p>
      <div className="flex justify-end gap-2">
        <Button variant="outline" size="sm" onClick={onClose}>İptal</Button>
        <Button size="sm" className="bg-red-600 hover:bg-red-700 text-white" onClick={onConfirm} data-testid="confirm-remove-member-btn">
          <Trash2 className="h-3.5 w-3.5 mr-1.5" />Çıkar
        </Button>
      </div>
    </div>
  </div>
);

const ContractorTeam = () => {
  const { token, user } = useAuth();
  const isAdmin = user?.org_memberships?.some(m => m.role === 'CONTRACTOR_ADMIN');
  const { t } = useTranslation();
  const [members, setMembers] = useState([]);
  const [invites, setInvites] = useState([]);
  const [loading, setLoading] = useState(true);
  const [inviteEmail, setInviteEmail] = useState('');
  const [inviteRole, setInviteRole] = useState('CONTRACTOR_ADMIN');
  const [inviting, setInviting] = useState(false);
  const [newInvite, setNewInvite] = useState(null);
  const [copied, setCopied] = useState(false);
  const [error, setError] = useState(null);
  const [removeTarget, setRemoveTarget] = useState(null);

  const load = async () => {
    setLoading(true);
    try {
      const res = await axios.get(`${API}/contractor/team`, { headers: { Authorization: `Bearer ${token}` } });
      setMembers(res.data.members || []);
      setInvites(res.data.invites || []);
    } catch { setError('Ekip verileri yüklenemedi.'); }
    finally { setLoading(false); }
  };

  useEffect(() => { if (token) load(); }, [token]); // eslint-disable-line

  const handleInvite = async (e) => {
    e.preventDefault();
    if (!inviteEmail.trim()) { setError(t('validation.required')); return; }
    if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(inviteEmail)) { setError(t('validation.invalidEmail')); return; }
    setInviting(true); setError(null); setNewInvite(null);
    try {
      const res = await axios.post(`${API}/contractor/team/invite`,
        { email: inviteEmail, role: inviteRole },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      setNewInvite(res.data);
      setInviteEmail('');
      await load();
    } catch (err) {
      setError(err?.response?.data?.detail || t('validation.required'));
    } finally { setInviting(false); }
  };

  const revokeInvite = async (inviteId) => {
    try {
      await axios.delete(`${API}/contractor/team/invite/${inviteId}`, { headers: { Authorization: `Bearer ${token}` } });
      setInvites(prev => prev.filter(i => i.id !== inviteId));
      toast.success('Davet iptal edildi');
    } catch { toast.error('Davet iptal edilemedi'); }
  };

  const changeRole = async (userId, newRole) => {
    try {
      await axios.patch(`${API}/contractor/team/member/${userId}/role`, { role: newRole }, { headers: { Authorization: `Bearer ${token}` } });
      toast.success('Rol güncellendi');
      setMembers(prev => prev.map(m => m.id === userId ? { ...m, role: newRole } : m));
    } catch (e) { toast.error(e.response?.data?.detail || 'Rol güncellenemedi'); }
  };

  const removeMember = async (userId) => {
    try {
      await axios.delete(`${API}/contractor/team/member/${userId}`, { headers: { Authorization: `Bearer ${token}` } });
      setMembers(prev => prev.filter(m => m.id !== userId));
      setRemoveTarget(null);
      toast.success('Üye çıkarıldı');
    } catch { toast.error('Üye çıkarılamadı'); }
  };

  const copyInviteLink = (code) => {
    const link = `${APP_URL}/contractor/signup?invite=${code}`;
    navigator.clipboard.writeText(link);
    setCopied(true); setTimeout(() => setCopied(false), 2000);
    toast.success('Davet linki kopyalandı!');
  };

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">Ekip Yönetimi</h1>
          <p className="text-sm text-gray-500 mt-0.5">Ekip üyelerinizi davet edin ve yönetin</p>
        </div>
        <div className="flex items-center gap-2 text-sm text-gray-500">
          <Users className="h-4 w-4" />
          <span className="font-semibold text-gray-900">{members.length}</span> üye
        </div>
      </div>

      {/* Ekip İstatistikleri */}
      {!loading && (
        <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
          {[
            { label: 'Toplam Üye', value: members.length, color: 'bg-green-50 text-green-700 border-green-100' },
            { label: 'Admin', value: members.filter(m => m.role === 'CONTRACTOR_ADMIN').length, color: 'bg-blue-50 text-blue-600 border-blue-100' },
            { label: 'Bekleyen Davet', value: invites.filter(i => new Date(i.expires_at) > new Date()).length, color: 'bg-yellow-50 text-yellow-600 border-yellow-100' },
            { label: 'Süresi Dolan', value: invites.filter(i => new Date(i.expires_at) <= new Date()).length, color: 'bg-gray-50 text-gray-500 border-gray-100' },
          ].map(stat => (
            <div key={stat.label} className={`rounded-xl border p-4 ${stat.color}`}>
              <p className="text-2xl font-bold">{stat.value}</p>
              <p className="text-xs font-medium mt-0.5 opacity-80">{stat.label}</p>
            </div>
          ))}
        </div>
      )}

      {error && (
        <div className="flex items-center gap-2 text-sm text-red-600 bg-red-50 border border-red-200 rounded-xl p-4">
          <AlertCircle className="h-4 w-4 flex-shrink-0" />{error}
        </div>
      )}

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* Davet Formu — sadece ADMIN görür */}
        {isAdmin && (
        <Card className="border-0 shadow-sm">
          <CardHeader className="pb-3 border-b border-gray-50">
            <CardTitle className="text-sm flex items-center gap-2">
              <UserPlus className="h-4 w-4 text-green-600" />Ekip Üyesi Davet Et
            </CardTitle>
          </CardHeader>
          <CardContent className="p-5">
            <form onSubmit={handleInvite} className="space-y-3">
              <div>
                <label className="text-xs font-semibold text-gray-500 block mb-1">E-posta Adresi *</label>
                <div className="relative">
                  <Mail className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-gray-400" />
                  <Input type="email" value={inviteEmail} onChange={e => setInviteEmail(e.target.value)}
                    placeholder="arkadas@sirket.com"
                    className="pl-9 h-10" data-testid="contractor-invite-email-input" />
                </div>
              </div>
              <div>
                <label className="text-xs font-semibold text-gray-500 block mb-1">Rol</label>
                <select value={inviteRole} onChange={e => setInviteRole(e.target.value)}
                  className="w-full h-10 px-3 border border-gray-200 rounded-md text-sm bg-white focus:outline-none focus:ring-2 focus:ring-green-500">
                  <option value="CONTRACTOR_ADMIN">Admin — Tam erişim</option>
                  <option value="CONTRACTOR_MEMBER">Üye — Sadece görüntüleme</option>
                </select>
              </div>
              <Button type="submit" disabled={inviting} data-testid="contractor-send-invite-btn"
                className="w-full h-10 bg-green-600 hover:bg-green-700 text-white font-semibold">
                {inviting ? <Loader2 className="h-4 w-4 animate-spin mr-2" /> : <UserPlus className="h-4 w-4 mr-2" />}
                Davet Linki Oluştur
              </Button>
            </form>

            {newInvite && (
              <div className="mt-4 bg-emerald-50 border border-emerald-200 rounded-xl p-4">
                <p className="text-xs font-bold text-emerald-800 mb-2 flex items-center gap-1.5">
                  <LinkIcon className="h-3.5 w-3.5" />Davet Linki Oluşturuldu!
                </p>
                <p className="text-xs text-emerald-700 mb-3"><strong>{newInvite.email}</strong> adresine bu linki paylaşın. 7 gün geçerli.</p>
                <div className="bg-white border border-emerald-300 rounded-lg p-2.5 font-mono text-xs text-gray-600 break-all mb-3">
                  {APP_URL}/contractor/signup?invite={newInvite.invite_code}
                </div>
                <Button size="sm" variant="outline" onClick={() => copyInviteLink(newInvite.invite_code)}
                  className="w-full h-8 text-xs border-emerald-300 text-emerald-700 hover:bg-emerald-50 gap-1.5">
                  {copied ? <Check className="h-3.5 w-3.5" /> : <Copy className="h-3.5 w-3.5" />}
                  {copied ? 'Kopyalandı!' : 'Davet Linkini Kopyala'}
                </Button>
              </div>
            )}
          </CardContent>
        </Card>
        )}

        {/* MEMBER için bilgi kartı */}
        {!isAdmin && (
          <Card className="border-0 shadow-sm bg-amber-50 border border-amber-200">
            <CardContent className="p-5 flex items-start gap-3">
              <AlertCircle className="h-5 w-5 text-amber-600 flex-shrink-0 mt-0.5" />
              <div>
                <p className="text-sm font-semibold text-amber-800">Üye Erişimi</p>
                <p className="text-xs text-amber-700 mt-1">Ekip üyesi davet etme yetkisi sadece Admin rolüne aittir. Erişim için ekip admininizle iletişime geçin.</p>
              </div>
            </CardContent>
          </Card>
        )}

        <Card className="border-0 shadow-sm">
          <CardHeader className="pb-3 border-b border-gray-50">
            <CardTitle className="text-sm flex items-center gap-2">
              <Clock className="h-4 w-4 text-green-600" />Bekleyen Davetler
              {invites.length > 0 && <span className="ml-auto bg-green-100 text-green-700 text-xs px-2 py-0.5 rounded-full font-bold">{invites.length}</span>}
            </CardTitle>
          </CardHeader>
          <CardContent className="p-0">
            {invites.length === 0 ? (
              <div className="p-6 text-center text-sm text-gray-400">Bekleyen davet yok</div>
            ) : (
              <div className="divide-y divide-gray-50">
                {invites.map(inv => (
                  <div key={inv.id} className="p-4 flex items-center justify-between gap-3">
                    <div className="flex-1 min-w-0">
                      <p className="text-sm font-semibold text-gray-900 truncate">{inv.email}</p>
                      <p className="text-xs text-gray-400 mt-0.5">
                        Kod: <span className="font-mono font-bold text-gray-600">{inv.invite_code}</span>
                        {' · '}Son: {inv.expires_at?.slice(0, 10)}
                      </p>
                    </div>
                    <div className="flex gap-2 flex-shrink-0">
                      <button onClick={() => copyInviteLink(inv.invite_code)}
                        className="w-7 h-7 flex items-center justify-center rounded-lg hover:bg-gray-100 transition-colors text-gray-400 hover:text-gray-600">
                        <Copy className="h-3.5 w-3.5" />
                      </button>
                      <button onClick={() => revokeInvite(inv.id)}
                        className="w-7 h-7 flex items-center justify-center rounded-lg hover:bg-red-50 transition-colors text-gray-400 hover:text-red-500">
                        <Trash2 className="h-3.5 w-3.5" />
                      </button>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </CardContent>
        </Card>
      </div>

      <Card className="border-0 shadow-sm">
        <CardHeader className="pb-3 border-b border-gray-50">
          <CardTitle className="text-sm flex items-center gap-2">
            <Users className="h-4 w-4 text-green-600" />Mevcut Üyeler
          </CardTitle>
        </CardHeader>
        <CardContent className="p-0">
          {loading ? (
            <div className="p-6 space-y-3">{[...Array(2)].map((_, i) => <div key={i} className="h-14 bg-gray-100 animate-pulse rounded-lg" />)}</div>
          ) : members.length === 0 ? (
            <div className="p-8 text-center text-sm text-gray-400">Henüz üye yok</div>
          ) : (
            <div className="divide-y divide-gray-50">
              {members.map(m => (
                <div key={m.id} data-testid={`contractor-team-member-${m.id}`} className="p-4 flex items-center gap-4">
                  <div className="w-10 h-10 rounded-xl bg-green-100 flex items-center justify-center text-green-700 font-bold text-sm flex-shrink-0">
                    {m.name?.[0]?.toUpperCase() || '?'}
                  </div>
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2">
                      <p className="text-sm font-semibold text-gray-900 truncate">{m.name}</p>
                      {m.is_self && <span className="text-xs bg-gray-100 text-gray-500 px-1.5 py-0.5 rounded-full">Siz</span>}
                    </div>
                    <p className="text-xs text-gray-400">{m.email}
                      {m.last_login_at && <span className="ml-2 text-gray-300">· Son giriş: {m.last_login_at}</span>}
                    </p>
                  </div>
                  <div className="flex items-center gap-2 flex-shrink-0">
                    {m.is_self ? (
                      <span className={`text-xs font-semibold px-2 py-0.5 rounded-full ${ROLE_COLORS[m.role] || 'bg-gray-100 text-gray-600'}`}>
                        {ROLE_LABELS[m.role] || m.role}
                      </span>
                    ) : (
                      <select value={m.role || ''} onChange={e => changeRole(m.id, e.target.value)}
                        className="text-xs font-semibold px-2 py-0.5 rounded-full border border-gray-200 bg-white text-gray-700 focus:outline-none focus:ring-1 focus:ring-green-400 cursor-pointer">
                        <option value="CONTRACTOR_ADMIN">Admin</option>
                        <option value="CONTRACTOR_MEMBER">Üye</option>
                      </select>
                    )}
                    {m.last_login_at && (
                      <span className="text-xs text-gray-400 hidden sm:block">{m.last_login_at}</span>
                    )}
                    {!m.is_self && (
                      <button onClick={() => setRemoveTarget(m)}
                        className="w-7 h-7 flex items-center justify-center rounded-lg hover:bg-red-50 transition-colors text-gray-300 hover:text-red-500"
                        data-testid={`remove-member-${m.id}`}>
                        <Trash2 className="h-3.5 w-3.5" />
                      </button>
                    )}
                  </div>
                </div>
              ))}
            </div>
          )}
        </CardContent>
      </Card>

      {removeTarget && (
        <RemoveConfirmModal
          member={removeTarget}
          onConfirm={() => removeMember(removeTarget.id)}
          onClose={() => setRemoveTarget(null)}
        />
      )}
    </div>
  );
};

export default ContractorTeam;

